import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Search, Filter, SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import './ShopPage.css';

const CATEGORIES = [
  { value: '', label: 'All Products' },
  { value: 'espresso', label: 'Espresso' },
  { value: 'filter', label: 'Filter Coffee' },
  { value: 'cold-brew', label: 'Cold Brew' },
  { value: 'capsule', label: 'Capsules' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'gift-sets', label: 'Gift Sets' },
];

const ROAST_LEVELS = ['light', 'medium', 'medium-dark', 'dark', 'extra-dark'];
const SORT_OPTIONS = [
  { value: '-createdAt', label: 'Newest' },
  { value: 'price', label: 'Price: Low to High' },
  { value: '-price', label: 'Price: High to Low' },
  { value: '-rating', label: 'Top Rated' },
];

export default function ShopPage() {
  const { category: urlCategory } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [filters, setFilters] = useState({
    category: urlCategory || '',
    search: '',
    roastLevel: '',
    minPrice: '',
    maxPrice: '',
    sort: '-createdAt',
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (urlCategory) setFilters(f => ({ ...f, category: urlCategory }));
  }, [urlCategory]);

  useEffect(() => {
    fetchProducts();
  }, [filters, page]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12, ...filters });
      Object.keys(filters).forEach(k => {
        if (!filters[k]) params.delete(k);
      });
      const res = await api.get(`/products?${params}`);
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
    if (key === 'category') {
      navigate(value ? `/shop/${value}` : '/shop', { replace: true });
    }
  };

  const clearFilters = () => {
    setFilters({ category: '', search: '', roastLevel: '', minPrice: '', maxPrice: '', sort: '-createdAt' });
    setPage(1);
    navigate('/shop', { replace: true });
  };

  const activeFilterCount = [filters.roastLevel, filters.minPrice, filters.maxPrice]
    .filter(Boolean).length;

  return (
    <div className="shop-page">
      <div className="shop-hero container">
        <h1>Our Coffee Collection</h1>
        <p>Discover {total} premium coffees from around the world</p>
      </div>

      <div className="shop-layout container">
        {/* Sidebar Filters */}
        <aside className={`shop-sidebar ${showFilters ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h3>Filters</h3>
            {activeFilterCount > 0 && (
              <button className="clear-filters" onClick={clearFilters}>
                <X size={14} /> Clear all
              </button>
            )}
          </div>

          <div className="filter-group">
            <label>Category</label>
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                className={`filter-option ${filters.category === cat.value ? 'active' : ''}`}
                onClick={() => handleFilterChange('category', cat.value)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="filter-group">
            <label>Roast Level</label>
            {ROAST_LEVELS.map(r => (
              <button
                key={r}
                className={`filter-option ${filters.roastLevel === r ? 'active' : ''}`}
                onClick={() => handleFilterChange('roastLevel', filters.roastLevel === r ? '' : r)}
              >
                {r.replace('-', ' ')}
              </button>
            ))}
          </div>

          <div className="filter-group">
            <label>Price Range (CHF)</label>
            <div className="price-inputs">
              <input
                type="number"
                placeholder="Min"
                value={filters.minPrice}
                onChange={e => handleFilterChange('minPrice', e.target.value)}
                className="form-control"
              />
              <span>—</span>
              <input
                type="number"
                placeholder="Max"
                value={filters.maxPrice}
                onChange={e => handleFilterChange('maxPrice', e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="shop-main">
          {/* Toolbar */}
          <div className="shop-toolbar">
            <div className="search-bar">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search coffees..."
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
              />
              {filters.search && <button onClick={() => handleFilterChange('search', '')}><X size={16} /></button>}
            </div>

            <div className="toolbar-right">
              <button className="filter-toggle-btn" onClick={() => setShowFilters(!showFilters)}>
                <SlidersHorizontal size={16} />
                Filters
                {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
              </button>
              <select
                value={filters.sort}
                onChange={e => handleFilterChange('sort', e.target.value)}
                className="sort-select"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="products-loading">
              {Array(6).fill(0).map((_, i) => <div key={i} className="product-skeleton" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="no-products">
              <h3>No products found</h3>
              <p>Try adjusting your filters or search terms</p>
              <button className="btn-primary" onClick={clearFilters}>Clear Filters</button>
            </div>
          ) : (
            <>
              <div className="results-info">{total} products</div>
              <div className="products-grid">
                {products.map(product => (
                  <ProductCard key={product._id} product={product} addItem={addItem} />
                ))}
              </div>

              {pages > 1 && (
                <div className="pagination">
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="page-btn">← Prev</button>
                  {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                    <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button disabled={page === pages} onClick={() => setPage(p => p + 1)} className="page-btn">Next →</button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function ProductCard({ product, addItem }) {
  return (
    <div className="product-card">
      <Link to={`/product/${product._id}`}>
        <div className="product-img-wrap">
          <img src={product.thumbnail || 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400'} alt={product.name} />
          {product.isFeatured && <span className="badge product-badge">Featured</span>}
        </div>
      </Link>
      <div className="product-body">
        <div className="product-category">{product.category}</div>
        <Link to={`/product/${product._id}`}><h3>{product.name}</h3></Link>
        {product.shortDescription && <p>{product.shortDescription}</p>}
        {product.rating > 0 && (
          <div className="product-rating">
            <span className="stars">{'★'.repeat(Math.round(product.rating))}</span>
            <span>({product.numReviews})</span>
          </div>
        )}
        <div className="product-footer">
          <span className="product-price">CHF {product.price.toFixed(2)}</span>
          <button
            className="btn-primary add-to-cart"
            onClick={() => addItem(product, 1)}
            disabled={product.stock === 0}
          >
            {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  );
}
